import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useImportExcelMutation } from '@app/api/Clients/api';
import {
  useImportExcelMutation as useImportServicesExcelMutation,
  useStopImportExcelMutation as useStopImportServicesExcelMutation,
} from '@app/api/ClientServices/api';
import {
  useImportExcelMutation as useImportProductsExcelMutation,
  useStopImportExcelMutation as useStopImportProductsExcelMutation,
} from '@app/api/Products/api';
import {
  useImportExcelMutation as useImportRecordsExcelMutation,
  useStopImportExcelMutation as useStopImportRecordsExcelMutation,
} from '@app/api/Records/api';
import {
  useImportExcelMutation as useImportEmployeesExcelMutation,
  useStopImportExcelMutation as useStopImportEmployeesExcelMutation,
} from '@app/api/Users/api';
import {
  CLIENT_SERVICES_PATH,
  CLIENTS_PATH,
  EMPLOYEES_PATH,
  PRODUCTS_PATH,
  RECORDS_PATH,
  WARNING,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { useStopImportClientsExcelMutation } from '@app/services/rtkQuery/Clients/api';
import {
  createWSConnection,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils';
import { clog } from '@app/utils/clog';
import { invokeWSMethod } from '@app/utils/createWSConnection';

import type { NImportFromExcel } from '@pages/Clients/ImportFromExcel/ImportFromExcel.types';

const PROGRESS_FLUSH_INTERVAL_MS = 300;

export const useImportFromExcel = () => {
  const [progress, setProgress] = useState(0);
  const [countCreate, setCountCreate] = useState(0);
  const [countUpdate, setCountUpdate] = useState(0);
  const [errors, setErrors] = useState<Array<NImportFromExcel.WsError>>([]);

  const { userId, database, activeBranchId } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);

  const isEmployeesPage = window.location.pathname.includes('employees');
  const isServicesPage = window.location.pathname.includes('services');
  const isClientsPage = window.location.pathname.includes('clients');
  const isRecordsPage = window.location.pathname.includes('records');
  const isProductsPage = window.location.pathname.includes(PRODUCTS_PATH);

  const navigate = useNavigate();

  const location = useLocation();

  const file = location.state?.file;
  const extension = location.state?.extension;
  const warehouseId = location.state?.warehouseId;
  // Флаг ставится после старта импорта вместо файла в history.state, чтобы
  // F5 на странице прогресса не отправлял файл на импорт повторно.
  const isImportStarted = Boolean(location.state?.isImportStarted);

  const isImportRequestedRef = useRef(false);

  const [
    stopImportClientsExcel,
    { isLoading: isStopImportClientsExcelLoading },
  ] = useStopImportClientsExcelMutation();
  const [
    stopImportRecordsExcel,
    { isLoading: isStopImportRecordsExcelLoading },
  ] = useStopImportRecordsExcelMutation();
  const [
    stopImportServicesExcel,
    { isLoading: isStopImportServicesExcelLoading },
  ] = useStopImportServicesExcelMutation();
  const [
    stopImportEmployeesExcel,
    { isLoading: isStopImportEmployeesExcelLoading },
  ] = useStopImportEmployeesExcelMutation();
  const [
    stopImportProductsExcel,
    { isLoading: isStopImportProductsExcelLoading },
  ] = useStopImportProductsExcelMutation();
  const [importExcel] = useImportExcelMutation();
  const [importRecordsByExcel] = useImportRecordsExcelMutation();
  const [importServicesByExcel] = useImportServicesExcelMutation();
  const [importEmployeesByExcel] = useImportEmployeesExcelMutation();
  const [importProductsByExcel] = useImportProductsExcelMutation();

  const checkFileExist = useCallback(
    (path: string) => {
      if (!file && !isImportStarted) {
        navigate(path);
      }

      return Boolean(file);
    },
    [file, isImportStarted, navigate]
  );

  useEffect(() => {
    // Импорт отправляется строго один раз: эффект перезапускается при смене
    // любой зависимости (ветка, колбэки), и без гарда файл уходил бы повторно.
    if (isImportRequestedRef.current || !activeBranchId) return;

    const updateEmployees = async () => {
      try {
        await importEmployeesByExcel({
          ActiveBranchId: activeBranchId,
          extension,
          data: file,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const updateServices = async () => {
      try {
        await importServicesByExcel({
          ActiveBranchId: activeBranchId,
          extension,
          data: file,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const updateClients = async () => {
      try {
        await importExcel({
          ActiveBranchId: activeBranchId,
          extension,
          data: file,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const updateRecords = async () => {
      try {
        await importRecordsByExcel({
          ActiveBranchId: activeBranchId,
          data: file,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const updateProducts = async () => {
      try {
        await importProductsByExcel({
          ActiveBranchId: activeBranchId,
          extension,
          data: file,
          warehouseId,
        }).unwrap();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const startImport = (path: string, update: () => Promise<void>) => {
      if (!checkFileExist(path)) return;

      isImportRequestedRef.current = true;
      update();

      // Убираем файл из history.state: он переживает перезагрузку страницы,
      // и после F5 импорт запускался бы заново.
      navigate(location.pathname, {
        replace: true,
        state: { isImportStarted: true },
      });
    };

    if (isEmployeesPage) {
      startImport(EMPLOYEES_PATH, updateEmployees);
    } else if (isServicesPage) {
      startImport(CLIENT_SERVICES_PATH, updateServices);
    } else if (isRecordsPage) {
      startImport(RECORDS_PATH, updateRecords);
    } else if (isProductsPage) {
      startImport(PRODUCTS_PATH, updateProducts);
    } else if (isClientsPage) {
      startImport(CLIENTS_PATH, updateClients);
    }
  }, [
    activeBranchId,
    checkFileExist,
    extension,
    file,
    importExcel,
    importEmployeesByExcel,
    importRecordsByExcel,
    importServicesByExcel,
    importProductsByExcel,
    isClientsPage,
    isEmployeesPage,
    isRecordsPage,
    isServicesPage,
    isProductsPage,
    location.pathname,
    navigate,
    showNotification,
    warehouseId,
  ]);

  useEffect(() => {
    if (
      !userId ||
      !database ||
      !(
        isClientsPage ||
        isRecordsPage ||
        isServicesPage ||
        isEmployeesPage ||
        isProductsPage
      )
    )
      return;

    const eventName = isRecordsPage
      ? 'RecordsImportProgressEvent'
      : isServicesPage
      ? 'ClientServicesImportProgressEvent'
      : isEmployeesPage
      ? 'UsersImportProgressEvent'
      : isProductsPage
      ? 'ProductItemsImportProgressEvent'
      : 'ClientsImportProgressEvent';

    // Бэкенд может прислать пачку событий подряд (каждое — с полным списком
    // ошибок). Рендерим только последнее и не чаще раза в
    // PROGRESS_FLUSH_INTERVAL_MS, иначе на больших файлах сотни перерисовок
    // списка ошибок подряд вешают вкладку.
    let latestData: NImportFromExcel.WsProgress | null = null;
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    let isUnmounted = false;

    const flush = () => {
      flushTimer = null;
      if (!latestData) return;

      const { progress, countCreate, countUpdate, errors } = latestData;
      latestData = null;

      setProgress(parseFloat(progress.toFixed(1)));
      setCountCreate(countCreate);
      setCountUpdate(countUpdate);
      setErrors(errors ?? []);
    };

    createWSConnection(async () => {
      await invokeWSMethod('ListenUser', database, userId);

      if (isUnmounted) return;

      clog('invokeWSMethod', 'ListenUser', database, userId);
      clog('subscribeToWsEvent', eventName);

      subscribeToWsEvent(eventName, (data: NImportFromExcel.WsProgress) => {
        if (!data) return;

        latestData = data;
        if (flushTimer === null) {
          flushTimer = setTimeout(flush, PROGRESS_FLUSH_INTERVAL_MS);
        }
      });
    });

    return () => {
      isUnmounted = true;
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
      }
      unsubscribeFromWsEvent(eventName);
    };
  }, [
    database,
    isClientsPage,
    isEmployeesPage,
    isProductsPage,
    isRecordsPage,
    isServicesPage,
    userId,
  ]);

  const stopClientsImport = useCallback(async () => {
    try {
      await stopImportClientsExcel({
        ActiveBranchId: activeBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [activeBranchId, showNotification, stopImportClientsExcel]);

  const stopRecordsImport = useCallback(async () => {
    try {
      await stopImportRecordsExcel({
        ActiveBranchId: activeBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [activeBranchId, showNotification, stopImportRecordsExcel]);

  const stopServicesImport = useCallback(async () => {
    try {
      await stopImportServicesExcel({
        ActiveBranchId: activeBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [activeBranchId, showNotification, stopImportServicesExcel]);

  const stopEmployeesImport = useCallback(async () => {
    try {
      await stopImportEmployeesExcel({
        ActiveBranchId: activeBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [activeBranchId, showNotification, stopImportEmployeesExcel]);

  const stopProductsImport = useCallback(async () => {
    try {
      await stopImportProductsExcel({
        ActiveBranchId: activeBranchId,
      }).unwrap();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [activeBranchId, showNotification, stopImportProductsExcel]);

  return {
    progress,
    countCreate,
    countUpdate,
    errors,
    isClientsPage,
    isEmployeesPage,
    isServicesPage,
    isRecordsPage,
    isProductsPage,
    hasImport: Boolean(file) || isImportStarted,
    stopClientsImport,
    isStopImportClientsExcelLoading,
    stopRecordsImport,
    isStopImportRecordsExcelLoading,
    stopServicesImport,
    isStopImportServicesExcelLoading,
    stopEmployeesImport,
    isStopImportEmployeesExcelLoading,
    stopProductsImport,
    isStopImportProductsExcelLoading,
  };
};
